
import { WalletRepository } from '@/repositories/WalletRepository';
import { UserRepository } from '@/repositories/UserRepository';
import { AuthRepository } from '@/repositories/AuthRepository';
import { PersonalityRepository } from '@/repositories/personality/PersonalityRepository';
import { YoungSchemasRepository } from '@/repositories/personality/YoungSchemasRepository';
import { ReferralRepository } from '@/repositories/social/ReferralRepository';
import { ConversationRepository } from '@/repositories/social/ConversationRepository';

/**
 * Repository Factory Class
 * Simplified factory pattern without complex generics
 */
export class RepositoryFactory {
  private static instance: RepositoryFactory;
  private walletRepository: WalletRepository | null = null;
  private userRepository: UserRepository | null = null;
  private authRepository: AuthRepository | null = null;
  private personalityRepository: PersonalityRepository | null = null;
  private youngSchemasRepository: YoungSchemasRepository | null = null;
  private referralRepository: ReferralRepository | null = null;
  private conversationRepository: ConversationRepository | null = null;

  // Repository tokens for type-safe dependency injection
  static TOKENS = {
    WALLET_REPOSITORY: 'WALLET_REPOSITORY',
    USER_REPOSITORY: 'USER_REPOSITORY',
    AUTH_REPOSITORY: 'AUTH_REPOSITORY',
    PERSONALITY_REPOSITORY: 'PERSONALITY_REPOSITORY',
    YOUNG_SCHEMAS_REPOSITORY: 'YOUNG_SCHEMAS_REPOSITORY',
    REFERRAL_REPOSITORY: 'REFERRAL_REPOSITORY',
    CONVERSATION_REPOSITORY: 'CONVERSATION_REPOSITORY',
  };

  private constructor() {
    // No dependencies needed
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): RepositoryFactory {
    if (!RepositoryFactory.instance) {
      RepositoryFactory.instance = new RepositoryFactory();
    }
    return RepositoryFactory.instance;
  }

  /**
   * Get wallet repository
   */
  public getWalletRepository(): WalletRepository {
    if (!this.walletRepository) {
      this.walletRepository = new WalletRepository();
    }
    return this.walletRepository;
  }

  /**
   * Get user repository
   */
  public getUserRepository(): UserRepository {
    if (!this.userRepository) {
      this.userRepository = new UserRepository();
    }
    return this.userRepository;
  }

  /**
   * Get auth repository
   */
  public getAuthRepository(): AuthRepository {
    if (!this.authRepository) {
      this.authRepository = new AuthRepository();
    }
    return this.authRepository;
  }

  /**
   * Get personality repository
   */
  public getPersonalityRepository(): PersonalityRepository {
    if (!this.personalityRepository) {
      this.personalityRepository = new PersonalityRepository();
    }
    return this.personalityRepository;
  }

  /**
   * Get Young Schemas repository
   */
  public getYoungSchemasRepository(): YoungSchemasRepository {
    if (!this.youngSchemasRepository) {
      this.youngSchemasRepository = new YoungSchemasRepository();
    }
    return this.youngSchemasRepository;
  }

  /**
   * Get referral repository
   */
  public getReferralRepository(): ReferralRepository {
    if (!this.referralRepository) {
      this.referralRepository = new ReferralRepository();
    }
    return this.referralRepository;
  }

  /**
   * Get conversation repository
   */
  public getConversationRepository(): ConversationRepository {
    if (!this.conversationRepository) {
      this.conversationRepository = new ConversationRepository();
    }
    return this.conversationRepository;
  }

  /**
   * Get repository by token (simplified version)
   */
  public getRepository<T>(token: string): T {
    switch (token) {
      case RepositoryFactory.TOKENS.WALLET_REPOSITORY:
        return this.getWalletRepository() as unknown as T;
      case RepositoryFactory.TOKENS.USER_REPOSITORY:
        return this.getUserRepository() as unknown as T;
      case RepositoryFactory.TOKENS.AUTH_REPOSITORY:
        return this.getAuthRepository() as unknown as T;
      case RepositoryFactory.TOKENS.PERSONALITY_REPOSITORY:
        return this.getPersonalityRepository() as unknown as T;
      case RepositoryFactory.TOKENS.YOUNG_SCHEMAS_REPOSITORY:
        return this.getYoungSchemasRepository() as unknown as T;
      case RepositoryFactory.TOKENS.REFERRAL_REPOSITORY:
        return this.getReferralRepository() as unknown as T;
      case RepositoryFactory.TOKENS.CONVERSATION_REPOSITORY:
        return this.getConversationRepository() as unknown as T;
      default:
        throw new Error(`Unknown repository token: ${token}`);
    }
  }
}
